import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Card, Row, Col, Button, Typography, Space, Spin, Progress, Drawer, InputNumber, Tag, Modal, Radio, DatePicker, Tooltip, Badge, Tabs, Select, message, Form, Pagination, Input } from 'antd';
import { UserAddOutlined, ReadOutlined, CopyOutlined, SearchOutlined, BookOutlined, RobotOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { useAuth } from '../contexts/AuthContext';
import useCoachCourses from '../hooks/useCoachCourses';
import CourseSelector from '../components/CourseSelector';
import MyCourseSelector from '../components/MyCourseSelector'; // 新增导入
import MemberSelector from '../components/MemberSelector';
import PostClassFeedback from '../components/PostClassFeedback';

const formatDate = 'YYYY-MM-DD';
const formatTime = 'HH:mm';

const { Text } = Typography;

const DEFAULT_CONSUME = 1;
const SLOT_DURATION_MIN = 30;
const DAY_START = '09:00';
const DAY_END = '21:00';

const getWeekStart = (date) => {
  const target = dayjs.isDayjs(date) ? date : dayjs(date);
  const normalized = target.startOf('day');
  return normalized.day() === 0
    ? normalized.subtract(6, 'day')
    : normalized.startOf('week').add(1, 'day');
};

// const { RangePicker } = DatePicker;

// Helper to build payload for a single member + single course at a specific datetime
const buildMemberPayload = ({ member, course, dateTime, period, creator, coach, id }) => {
  const combined = dateTime.second(0);

  return {
    accClasses: 0,
    babyId: member.id,
    babyName: member.babyName,
    briefSummary: '',
    coach: coach,
    completion: '',
    consumeClass: period || DEFAULT_CONSUME,
    createUser: creator,
    customerName: member.customerName || '',
    details: course
      ? [{
          sportsItem: course.eventTitle,
          sportsContent: course.courseContext || '',
        }]
      : [],
    heartRate: '',
    id: id && typeof id === 'number' ? id : null,
    points: '',
    restTimes: '',
    suggestion: '',
    trainDate: combined.format('YYYY-MM-DD HH:mm'),
    trainTimes: period || DEFAULT_CONSUME,
    updateUser: creator,
    teachingMode: member.deliveryType || '一对一',
  };
};

// 根据deliveryType值返回对应的显示文本
const getDisplayDeliveryType = (deliveryType) => {
  switch (deliveryType) {
    case '一对一':
      return 'SVIP专属';
    case '一对二':
      return 'VIP私教';
    case '一对多':
      return '私教小团';
    default:
      return '私教小团';
  }
};

// Generate 30-minute slots for a given date
const generateTimeSlots = (date) => {
  const [startHour, startMinute] = DAY_START.split(':').map(Number);
  const [endHour, endMinute] = DAY_END.split(':').map(Number);

  const start = dayjs(date).hour(startHour).minute(startMinute).second(0);
  const end = dayjs(date).hour(endHour).minute(endMinute).second(0);

  const slots = [];
  let current = start;
  while (current.isBefore(end) || current.isSame(end)) {
    slots.push({
      slotId: current.format(formatTime),
      startTime: current,
      label: current.format(formatTime),
    });
    current = current.add(SLOT_DURATION_MIN, 'minute');
  }
  return slots;
};

// Build week days from weekStart
const buildWeekDays = (weekStart) => {
  const weekDays = [];
  const weekLabels = ['周一', '周二', '周三', '周四', '周五', '周六', '周日'];
  for (let i = 0; i < 7; i += 1) {
    const d = weekStart.add(i, 'day');
    weekDays.push({
      date: d,
      label: weekLabels[i] || d.format('ddd'),
    });
  }
  return weekDays;
};

// Map backend pageList data to local schedule rows structure
const mapPageListToRows = (list) => {
  const allRows = {};
  if (!Array.isArray(list)) return allRows;

  list.forEach((item) => {
    if (!item.trainDate) return;
    const dt = dayjs(item.trainDate);
    if (!dt.isValid()) return;
    const dateKey = dt.format(formatDate);

    if (!allRows[dateKey]) allRows[dateKey] = {};

    const rows = allRows[dateKey];
    const slotId = dt.format(formatTime);
    if (!rows[slotId]) {
      rows[slotId] = {
        slotId,
        startTime: dt,
        period: Number(item.trainTimes) || DEFAULT_CONSUME,
        entries: [],
      };
    }

    const row = rows[slotId];

    // Find or create a single entry per row for simplicity (all members同一组课)
    let entry = row.entries.find(e => e.id === item.id);
    if (!entry) {
      entry = {
        id: item.id,
        members: [],
        courses: [],
        isExisting: true,
        updateFlag: 0,
        completeClass: item.completeClass || '',
      };
      row.entries.push(entry);
    } else {
      // 如果entry已存在，也更新completeClass字段
      entry.completeClass = item.completeClass || '';
    }

    // Member
    const member = {
      id: item.babyId,
      babyName: item.babyName,
      customerName: item.customerName,
      customerId: item.customerId,
      classId: item.classId,
      deliveryType: item.teachingMode,
    };
    if (!entry.members.find((m) => m.id === member.id)) {
      entry.members.push(member);
    }

    // Courses from details
    if (Array.isArray(item.details)) {
      item.details.forEach((d) => {
        const courseKey = `${d.sportsItem || ''}-${d.sportsContent || ''}`;
        if (!entry.courses.find((c) => c._key === courseKey)) {
          entry.courses.push({
            _key: courseKey,
            courseKbn: d.sportsItem,
            eventTitle: d.sportsItem,
            courseContext: d.sportsContent,
          });
        }
      });
    }
  });

  return allRows;
};

const SingleMemberScheduler = ({ user, memberId, memberInfo, onMemberChange, onSaveSuccess, phase, onDataLoad }) => {
  const aiCoachMessageKeyRef = useRef('ai-coach-loading');
  const { apiClient } = useAuth();

  // 设置 dayjs 为中文
  dayjs.locale('zh-cn');

  const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
  const coach = storedUser.coach || '';

  const [currentDate, setCurrentDate] = useState(() => dayjs().startOf('day'));
  const [weekStart, setWeekStart] = useState(() => getWeekStart(dayjs()));
  const [pageLoading, setPageLoading] = useState(false);

  const [scheduleRows, setScheduleRows] = useState([]); // 改为数组存储分页数据
  
  // 分页状态
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSize] = useState(12); // 固定每页12行
  
  // 计算总页数
  const totalPages = Math.ceil(totalRecords / pageSize);
  
  // 会员ID参数（用于过滤特定会员的排课）
  const [babyId, setBabyId] = useState(null);

  // 当传入的 memberId 变化时，更新 babyId 状态
  useEffect(() => {
    if (memberId) {
      setBabyId(memberId);
    } else {
      setBabyId(null);
    }
  }, [memberId]);

  // Drawer state for selectors
  const [activeRowSlotId, setActiveRowSlotId] = useState(null);
  const [activeSelectorType, setActiveSelectorType] = useState(null); // 'member' | 'course' | null

  // New state for my course selector modal
  const [myCourseSelectorModalVisible, setMyCourseSelectorModalVisible] = useState(false);

  // Saving state and progress
  const [saving, setSaving] = useState(false);
  const [saveProgress, setSaveProgress] = useState({ completed: 0, total: 0, currentMemberName: '' });

  // Copy modal state
  const [copyModalVisible, setCopyModalVisible] = useState(false);
  const [copyTargetDate, setCopyTargetDate] = useState(null);
  const [copyMode, setCopyMode] = useState('overwrite');

  // Copy course confirmation modal
  const [copyCourseModal, setCopyCourseModal] = useState(false);
  const [copySlotId, setCopySlotId] = useState(null);

  // Delete entry modal state
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteSlotId, setDeleteSlotId] = useState(null);
  const [deleteEntryId, setDeleteEntryId] = useState(null);

    // Remove member modal state
    const [removeMemberModalVisible, setRemoveMemberModalVisible] = useState(false);
    const [removeMemberSlotId, setRemoveMemberSlotId] = useState(null);
    const [removeMemberId, setRemoveMemberId] = useState(null);
    const [removeMember, setRemoveMember] = useState(null);

    // Single save modal state
    const [singleSaveModalVisible, setSingleSaveModalVisible] = useState(false);
    const [singleSaveSlotId, setSingleSaveSlotId] = useState(null);
    const [singleSaveEntryId, setSingleSaveEntryId] = useState(null);

    // Active tab state
    const [activeTab, setActiveTab] = useState('offline'); // 'offline' or 'home'
    const [printing, setPrinting] = useState(false);

    // Training feedback modal state
    const [trainingFeedbackModalVisible, setTrainingFeedbackModalVisible] = useState(false);
    const [currentTrainingRecord, setCurrentTrainingRecord] = useState(null);

    // Training feedback form
    const [trainingFeedbackForm] = Form.useForm();

    // Ref for PDF export
    const trainingFeedbackScheduleRef = useRef();
    const schedulePrintRef = useRef(null);
    const scheduleScrollRef = useRef(null);

    // Future scheduling modal state
    const [futureSchedulingModalVisible, setFutureSchedulingModalVisible] = useState(false);
    const [futureSchedules, setFutureSchedules] = useState([]);
    const [disabledDates, setDisabledDates] = useState([]);
    const [selectedDates, setSelectedDates] = useState([]);
    const [datePickerOpen, setDatePickerOpen] = useState(true);
    const [schedulingEntry, setSchedulingEntry] = useState(null);
    
    // Change time modal state - 按照ScheduCourse.js的逻辑
    const [changeTimeModalVisible, setChangeTimeModalVisible] = useState(false);
    const [changeTimeSlotId, setChangeTimeSlotId] = useState(null);
    const [changeTimeEntryId, setChangeTimeEntryId] = useState(null);
    const [selectedNewDate, setSelectedNewDate] = useState(null);
    const [selectedNewTime, setSelectedNewTime] = useState(null);

    // AI Coach state
    const [aiCoachLoading, setAiCoachLoading] = useState({}); // Track loading state for each slot
    const [aiCoachModalVisible, setAiCoachModalVisible] = useState(false);
    const [aiCoachPreparing, setAiCoachPreparing] = useState(false);
    const [aiCoachModalSaving, setAiCoachModalSaving] = useState(false);
    const [aiCoachRows, setAiCoachRows] = useState([]);

  const weekEnd = useMemo(() => weekStart.add(6, 'day'), [weekStart]);

  const weekDays = useMemo(() => buildWeekDays(weekStart), [weekStart]);

  const timeSlots = useMemo(() => generateTimeSlots(currentDate), [currentDate]);
  const aiCoachTimeOptions = useMemo(() => timeSlots.map((slot) => ({ label: slot.label, value: slot.slotId })), [timeSlots]);

  const getAiCoachMember = () => {
    if (memberInfo && (memberInfo.id || memberInfo.babyId)) {
      return {
        id: memberInfo.id || memberInfo.babyId,
        babyName: memberInfo.babyName || memberInfo.name || '',
        customerId: memberInfo.customerId || memberInfo.orgId || (storedUser.customerId ? storedUser.customerId.split('-')[0] : ''),
        customerName: memberInfo.customerName || '',
        deliveryType: memberInfo.deliveryType || memberInfo.teachingMode || '一对一',
      };
    }

    const recordWithMember = scheduleRows.find((item) => item?.babyId);
    if (recordWithMember) {
      return {
        id: recordWithMember.babyId,
        babyName: recordWithMember.babyName,
        customerId: recordWithMember.customerId,
        customerName: recordWithMember.customerName,
        deliveryType: recordWithMember.teachingMode || '一对一',
      };
    }

    if (babyId) {
      return {
        id: babyId,
        babyName: memberInfo?.babyName || '当前会员',
        customerId: memberInfo?.customerId || (storedUser.customerId ? storedUser.customerId.split('-')[0] : ''),
        customerName: memberInfo?.customerName || '',
        deliveryType: memberInfo?.deliveryType || '一对一',
      };
    }

    return null;
  };

  const getAiCoachRowCount = () => {
    const remainder = totalRecords % 12;
    return remainder === 0 ? 12 : 12 - remainder;
  };

  const parseAiCoachPlan = (planData) => {
    if (!planData) return '';
    return planData
      .split(/;|；/)
      .map((item) => item.trim())
      .filter(Boolean)
      .map((item) => {
        const [title, ...rest] = item.split('-');
        const detail = rest.join('-').trim();
        return detail ? `${title.trim()} - ${detail}` : title.trim();
      })
      .join('\n');
  };

  const requestAiCoachContent = async (member, offset = 0) => {
    const targetDate = dayjs().add(offset, 'day');
    const payload = {
      customerId: member.customerId,
      babyId: member.id,
      trainDate: targetDate.format('YYYY-MM-DD'),
    };

    const resp = await apiClient.post('/sports-server-prod/taller.api.v1/aicoach/generatePlan', payload);
    if (resp?.data?.code !== 200) {
      throw new Error(resp?.data?.msg || '生成计划失败');
    }
    return parseAiCoachPlan(resp.data.data || '');
  };

  const handleOpenAiCoachModal = async () => {
    if (!apiClient) return;
    if (aiCoachPreparing) {
      message.info('AI排课中，请稍等');
      return;
    }

    const member = getAiCoachMember();
    if (!member || !member.id || !member.customerId) {
      message.error('未找到完整的会员信息，无法生成AI排课');
      return;
    }

    const rowCount = Math.min(getAiCoachRowCount(), 12);
    if (rowCount <= 0) {
      message.warning('暂无可生成的AI排课明细');
      return;
    }

    setAiCoachRows([]);
    setAiCoachPreparing(true);
    message.loading({ content: 'AI排课中，请稍等', key: aiCoachMessageKeyRef.current, duration: 0 });

    try {
      const generatedRows = [];
      for (let i = 0; i < rowCount; i += 1) {
        const content = await requestAiCoachContent(member, i);
        
        // 将内容解析为details格式，只保留sportsItem，隐藏sportsContent
        const details = normalizeAiCoachDetails(content).map(detail => ({
          sportsItem: detail.sportsItem,
          sportsContent: detail.sportsContent // 隐藏存储
        }));
        
        generatedRows.push({
          key: `ai-${Date.now()}-${i}`,
          trainDate: null,
          trainTime: '',
          content: content, // 原始内容用于显示
          details: details // 解析后的详情用于展示和操作
        });
      }
      setAiCoachRows(generatedRows);
      setAiCoachModalVisible(true);
    } catch (error) {
      console.error('AI教练生成失败:', error);
      message.error(error?.message || 'AI生成失败，请稍后重试');
    } finally {
      setAiCoachPreparing(false);
      message.destroy(aiCoachMessageKeyRef.current);
    }
  };

  const handleAiCoachRowFieldChange = (key, field, value) => {
    setAiCoachRows((prev) => prev.map((row) => (row.key === key ? { ...row, [field]: value } : row)));
  };

  const handleAiCoachRowDelete = (key) => {
    setAiCoachRows((prev) => prev.filter((row) => row.key !== key));
  };

  // 处理AI教练详情项删除
  const handleAiCoachDetailDelete = (rowKey, detailIndex) => {
    setAiCoachRows((prev) => 
      prev.map((row) => {
        if (row.key === rowKey) {
          const newDetails = [...(row.details || [])];
          newDetails.splice(detailIndex, 1);
          return { ...row, details: newDetails };
        }
        return row;
      })
    );
  };
  const normalizeAiCoachDetails = (content) => {
    const segments = (content || '')
      .split(/\n|;|；/)
      .map((item) => item.trim())
      .filter(Boolean);
    if (!segments.length) {
      return [{ sportsItem: 'AI课程', sportsContent: '' }];
    }
    return segments.map((segment) => {
      const [item, ...rest] = segment.split('-');
      return {
        sportsItem: (item || 'AI课程').trim(),
        sportsContent: rest.join('-').trim(),
      };
    });
  };

  const validateAiCoachRows = () => {
    if (!aiCoachRows.length) {
      message.warning('暂无AI排课明细');
      return false;
    }
    const hasInvalid = aiCoachRows.some((row) => !row.trainDate || !row.trainTime || !row.content?.trim());
    if (hasInvalid) {
      message.warning('有内容未填充，请先检查');
      return false;
    }
    return true;
  };

  const handleAiCoachModalSave = async () => {
    if (!apiClient) return;
    const member = getAiCoachMember();
    if (!member) {
      message.error('未找到会员信息，无法保存');
      return;
    }

    const creator = user?.name || storedUser.name || '';
    const payload = aiCoachRows.map((row) => ({
      accClasses: 0,
      babyId: member.id,
      babyName: member.babyName || '',
      briefSummary: '',
      coach,
      completion: '',
      consumeClass: 1,
      createUser: creator,
      customerName: member.customerName || '',
      // 使用details字段，符合项目规范和明细行展示要求
      details: row.details || [],
      heartRate: '',
      id: null,
      points: '',
      restTimes: '',
      suggestion: '',
      trainDate: `${row.trainDate.format('YYYY-MM-DD')} ${row.trainTime}`,
      trainTimes: 1,
      updateUser: creator,
      teachingMode: member.deliveryType || '一对一',
      courseKbn: activeTab === 'offline' ? '线下课程' : '家庭训练',
    }));

    setAiCoachModalSaving(true);
    try {
      const resp = await apiClient.post('/sports-server-prod/taller.api.v1/prescription/addBatch', payload);
      if (resp?.data?.code !== 200) {
        throw new Error(resp?.data?.msg || '保存失败');
      }
      message.success('AI排课保存成功');
      setAiCoachModalVisible(false);
      setAiCoachRows([]);
      loadWeekData(1);
    } catch (error) {
      message.error(error?.message || '保存失败，请稍后重试');
    } finally {
      setAiCoachModalSaving(false);
    }
  };
  const handleConfirmAiCoachSave = () => {
    if (!validateAiCoachRows()) return;
    Modal.confirm({
      title: '确认保存AI排课？',
      content: '确认后将批量保存这些AI生成的排课。',
      onOk: handleAiCoachModalSave,
    });
  };

  const handleCloseAiCoachModal = () => {
    if (aiCoachModalSaving) return;
    setAiCoachModalVisible(false);
    setAiCoachRows([]);
  };

  // currentRows 现在直接指向分页数据数组
  const currentRows = scheduleRows;

  const closeDrawer = () => {
    setActiveRowSlotId(null);
    setActiveSelectorType(null);
  };

  const handlePrevWeek = () => {
    const newStart = weekStart.subtract(1, 'week');
    setWeekStart(newStart);
    setCurrentDate(newStart);
  };

  const handleNextWeek = () => {
    const newStart = weekStart.add(1, 'week');
    setWeekStart(newStart);
    setCurrentDate(newStart);
  };

  const handleSelectDate = (date) => {
    if (!date) return;
    const normalized = date.startOf('day');
    setCurrentDate(normalized);
    setWeekStart(getWeekStart(normalized));
  };

    const handleTabChange = (key) => {
    setActiveTab(key);
    };

    const handlePrintSchedule = async () => {
    if (!schedulePrintRef.current) {
      message.error('无法打印排课表：页面未准备好');
      return;
    }
    setPrinting(true);
    const scrollEl = scheduleScrollRef.current;
    const originalStyles = scrollEl
      ? {
          maxHeight: scrollEl.style.maxHeight,
          overflow: scrollEl.style.overflow,
          overflowY: scrollEl.style.overflowY,
        }
      : null;

    try {
      if (scrollEl) {
        scrollEl.style.maxHeight = 'none';
        scrollEl.style.overflow = 'visible';
        scrollEl.style.overflowY = 'visible';
      }
      await new Promise((resolve) => requestAnimationFrame(resolve));
      const canvas = await html2canvas(schedulePrintRef.current, {
        scale: 2,
        useCORS: true,
        scrollY: -window.scrollY,
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const printableWidth = pageWidth - margin * 2;
      const printableHeight = pageHeight - margin * 2;
      const imgHeight = (canvas.height * printableWidth) / canvas.width;
      let heightLeft = imgHeight - printableHeight;

      pdf.addImage(imgData, 'PNG', margin, margin, printableWidth, imgHeight);

      while (heightLeft > 0) {
        const position = margin - (imgHeight - heightLeft);
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', margin, position, printableWidth, imgHeight);
        heightLeft -= printableHeight;
      }

      const tabName = activeTab === 'offline' ? '线下课程' : '家庭训练';
      pdf.save(`${currentDate.format('YYYYMMDD')}-${tabName}.pdf`);
      message.success('排课表已导出');
    } catch (error) {
      console.error('打印排课表失败:', error);
      message.error('打印排课表失败，请稍后重试');
    } finally {
      if (scrollEl && originalStyles) {
        scrollEl.style.maxHeight = originalStyles.maxHeight || '';
        scrollEl.style.overflow = originalStyles.overflow || '';
        scrollEl.style.overflowY = originalStyles.overflowY || '';
      }
      setPrinting(false);
    }
    };

  const handleOpenTrainingFeedback = (entry) => {
    // 设置当前训练记录
    setCurrentTrainingRecord(entry);
    setTrainingFeedbackModalVisible(true);
  };


  const handleConfirmScheduling = () => {
    if (selectedDates.length === 0) {
      message.warning('请选择至少一个日期');
      return;
    }
    Modal.confirm({
      title: '确认排课',
      content: `确定要为 ${schedulingEntry.members[0].babyName} 排课 ${selectedDates.length} 个日期吗？`,
      onOk: async () => {
        await handleScheduleConfirm();
      },
    });
  };

  const handleScheduleConfirm = async () => {
    const member = schedulingEntry.members[0];
    const creator = user?.name || storedUser.name || '';
    const coachId = coach;
    
    // 从原始调度条目中获取第一个有效的课程详情和时间信息
    const originalDetails = schedulingEntry.courses && schedulingEntry.courses.length > 0 
      ? schedulingEntry.courses.map(course => ({
          sportsItem: course.eventTitle,
          sportsContent: course.courseContext || '',
        }))
      : [
          {
            sportsItem: "核心/俯地收腹",
            sportsContent: ""
          },
          {
            sportsItem: "有氧/小碎步行进",
            sportsContent: ""
          }
        ];
    
    // 从原始调度条目中获取时间信息
    const originalTrainTime = schedulingEntry.trainDate ? dayjs(schedulingEntry.trainDate).format('HH:mm') : '20:00';
    
    for (const date of selectedDates) {
      const payload = {
        accClasses: 0,
        babyId: member.id,
        babyName: member.babyName,
        briefSummary: '',
        coach: coachId,
        completion: '',
        consumeClass: 1,
        createUser: creator,
        customerName: member.customerName || '',
        details: originalDetails,
        heartRate: '',
        id: null,
        points: '',
        restTimes: '',
        suggestion: '',
        trainDate: date.format(`YYYY-MM-DD ${originalTrainTime}`),
        trainTimes: 1,
        updateUser: creator,
        teachingMode: member.deliveryType || 'VIP私教',
        courseKbn: "线下课程"
      };
      try {
        await apiClient.post('/sports-server-prod/taller.api.v1/prescription/add', payload);
      } catch (error) {
        message.error(`排课失败：${date.format('YYYY-MM-DD')}`);
        return;
      }
    }
    message.success('排课成功');
    setFutureSchedulingModalVisible(false);
    // 刷新数据
    loadWeekData();
  };

  const handleRowAddMember = (slotId) => {
    setActiveRowSlotId(slotId);
    setActiveSelectorType((prev) => (prev === 'member' && activeRowSlotId === slotId ? null : 'member'));
  };

  const handleRowAddCourse = (slotId) => {
    setActiveRowSlotId(slotId);
    setActiveSelectorType((prev) => (prev === 'course' && activeRowSlotId === slotId ? null : 'course'));
  };

  const handleRowAddMyCourse = (slotId) => {
    setActiveRowSlotId(slotId);
    setMyCourseSelectorModalVisible(true);
  };

  const getRowMembers = useCallback((slotId) => {
    const row = currentRows[slotId];
    if (!row || !Array.isArray(row.entries)) return [];
    const ids = [];
    row.entries.forEach((e) => {
      (e.members || []).forEach((m) => {
        if (!ids.includes(m.id)) ids.push(m.id);
      });
    });
    return ids;
  }, [currentRows]);

  const handleMemberSelect = (member) => {
    if (!activeRowSlotId || !member) return;
    setScheduleRows((prev) => {
      const dateKey = currentDate.format(formatDate);
      const updatedRows = { ...prev[dateKey] };
      const slotId = activeRowSlotId;
      let row = updatedRows[slotId];
      if (!row) {
        const slot = timeSlots.find((s) => s.slotId === slotId);
        row = {
          slotId,
          startTime: slot ? slot.startTime : dayjs(currentDate.format(`${formatDate} ${slotId}`)),
          period: DEFAULT_CONSUME,
          entries: [],
        };
        updatedRows[slotId] = row;
      }

      const totalMembers = row.entries.reduce((acc, e) => acc + (e.members || []).length, 0);
      const exists = (row.entries[0]?.members || []).find((m) => m.id === member.id);

      if (exists) {
        // 检查是否存在已保存的记录（isExisting为true 或者有数字类型的ID）
        const hasSavedRecord = row.entries.some(entry => entry.isExisting === true || (entry.id && typeof entry.id === 'number'));
        
        if (hasSavedRecord) {
          // 如果存在已保存的记录，则显示确认弹框
          setRemoveMemberSlotId(slotId);
          setRemoveMemberId(member.id);
          setRemoveMember(member);
          setRemoveMemberModalVisible(true);
          return prev;
        } else {
          // 如果没有已保存的记录，直接从本地状态中移除
          row.entries.forEach((entry) => {
            entry.members = (entry.members || []).filter((m) => m.id !== member.id);
          });
          
          // 清理空的entry
          const filteredEntries = row.entries.filter(entry => (entry.members || []).length > 0);
          if (filteredEntries.length === 0) {
            delete updatedRows[slotId];
          } else {
            row.entries = filteredEntries;
          }
          
          return { ...prev, [dateKey]: updatedRows };
        }
      }

      if (totalMembers >= 4) {
        message.warning('每个时间段最多选择4个会员');
        return prev;
      }
      // ensure at least one entry
      if (!row.entries.length) {
        row.entries.push({
          id: `entry-${slotId}-0`,
          members: [],
          courses: [],
          isExisting: false,
          updateFlag: 0,
        });
      }
      row.entries[0].members = row.entries[0].members || [];
      if (!row.entries[0].members.find((m) => m.id === member.id)) {
        row.entries[0].members.push(member);
        row.entries[0].updateFlag = 1;
      }
      return { ...prev, [dateKey]: updatedRows };
    });
  };

  const handleCoursesChange = (courses) => {
    if (!activeRowSlotId) return;
    setScheduleRows((prev) => {
      const dateKey = currentDate.format(formatDate);
      const updatedRows = { ...prev[dateKey] };
      const slotId = activeRowSlotId;
      let row = updatedRows[slotId];
      if (!row) {
        const slot = timeSlots.find((s) => s.slotId === slotId);
        row = {
          slotId,
          startTime: slot ? slot.startTime : dayjs(currentDate.format(`${formatDate} ${slotId}`)),
          period: DEFAULT_CONSUME,
          entries: [],
        };
        updatedRows[slotId] = row;
      }

      const uniqueCourses = [];
      (courses || []).forEach((c) => {
        if (!uniqueCourses.find((u) => (u.seq && c.seq && u.seq === c.seq) || (u._key && c._key && u._key === c._key))) {
          uniqueCourses.push(c);
        }
      });

      if (!row.entries.length) {
        row.entries.push({
          id: `entry-${slotId}-0`,
          members: [],
          courses: [],
          isExisting: false,
          updateFlag: 0,
        });
      }

      row.entries[0].courses = uniqueCourses;
      row.entries[0].updateFlag = 1;
      return { ...prev, [dateKey]: updatedRows };
    });
  };

  // 处理动作资源库选择
  const handleCourseSelectFromLibrary = (courses) => {
    if (!activeRowSlotId) return;
    
    setScheduleRows((prev) => {
      const dateKey = currentDate.format(formatDate);
      const updatedRows = { ...prev[dateKey] };
      const slotId = activeRowSlotId;
      let row = updatedRows[slotId];
      if (!row) {
        const slot = timeSlots.find((s) => s.slotId === slotId);
        row = {
          slotId,
          startTime: slot ? slot.startTime : dayjs(currentDate.format(`${formatDate} ${slotId}`)),
          period: DEFAULT_CONSUME,
          entries: [],
        };
        updatedRows[slotId] = row;
      }

      // 确保至少有一个entry
      if (!row.entries.length) {
        row.entries.push({
          id: `entry-${slotId}-0`,
          members: [],
          courses: [],
          isExisting: false,
          updateFlag: 0,
        });
      }

      // 转换选中的课程格式
      const courseActions = courses.map(course => ({
        _key: `${course.seq}-${course.eventTitle}`,
        seq: course.seq,
        eventTitle: course.eventTitle,
        courseContext: course.courseContext || '',
      }));

      row.entries[0].courses = courseActions;
      row.entries[0].updateFlag = 1;
      return { ...prev, [dateKey]: updatedRows };
    });
    
    // 关闭选择器
    closeDrawer();
  };

  // 新增：处理我的课程选择
  const handleMyCourseSelect = (selectedCourse) => {
    if (!activeRowSlotId || !selectedCourse) return;
    
    // 提取选中课程的关联动作
    const courseActions = [];
    
    if (selectedCourse.relateActions && Array.isArray(selectedCourse.relateActions)) {
      // 如果课程有关联动作数组
      selectedCourse.relateActions.forEach(action => {
        courseActions.push({
          _key: `${action.seq}-${action.eventTitle}`,
          seq: action.seq,
          eventTitle: action.eventTitle,
          courseContext: action.courseContext || '',
          courseDesc: selectedCourse.courseDesc, // 保留课程描述
          coursePrice: selectedCourse.coursePrice, // 保留课程价格
        });
      });
    } else if (selectedCourse.relateAction && selectedCourse.relateActionShow) {
      // 如果使用新的relateAction和relateActionShow字段
      const seqArray = selectedCourse.relateAction ? selectedCourse.relateAction.split(',') : [];
      const eventTitleArray = selectedCourse.relateActionShow ? selectedCourse.relateActionShow.split(',') : [];
      
      seqArray.forEach((seq, index) => {
        const eventTitle = eventTitleArray[index];
        if (eventTitle) {
          courseActions.push({
            _key: `${seq}-${eventTitle}`,
            seq: seq,
            eventTitle: eventTitle,
            courseContext: '', // 从关联动作中可能没有上下文信息
            courseDesc: selectedCourse.courseDesc, // 保留课程描述
            coursePrice: selectedCourse.coursePrice, // 保留课程价格
          });
        }
      });
    }

    // 更新当前行的课程 - 清除原有课程，替换为新选择的课程动作
    setScheduleRows((prev) => {
      const dateKey = currentDate.format(formatDate);
      const updatedRows = { ...prev[dateKey] };
      const slotId = activeRowSlotId;
      let row = updatedRows[slotId];
      if (!row) {
        const slot = timeSlots.find((s) => s.slotId === slotId);
        row = {
          slotId,
          startTime: slot ? slot.startTime : dayjs(currentDate.format(`${formatDate} ${slotId}`)),
          period: DEFAULT_CONSUME,
          entries: [],
        };
        updatedRows[slotId] = row;
      }

      if (!row.entries.length) {
        row.entries.push({
          id: `entry-${slotId}-0`,
          members: [],
          courses: [],
          isExisting: false,
          updateFlag: 0,
        });
      }

      // 清除原有课程，替换为新选择的课程动作
      row.entries[0].courses = courseActions;
      row.entries[0].updateFlag = 1;
      return { ...prev, [dateKey]: updatedRows };
    });

    // 关闭模态框
    setMyCourseSelectorModalVisible(false);
  };

  const handlePeriodChange = (slotId, value) => {
    const period = Number(value) || DEFAULT_CONSUME;
    setScheduleRows((prev) => {
      const dateKey = currentDate.format(formatDate);
      const updatedRows = { ...prev[dateKey] };
      let row = updatedRows[slotId];
      if (!row) {
        const slot = timeSlots.find((s) => s.slotId === slotId);
        row = {
          slotId,
          startTime: slot ? slot.startTime : dayjs(currentDate.format(`${formatDate} ${slotId}`)),
          period,
          entries: [],
        };
        updatedRows[slotId] = row;
      } else {
        row.period = period;
        if (row.entries.length > 0) {
          row.entries[0].updateFlag = 1;
        }
      }
      return { ...prev, [dateKey]: updatedRows };
    });
  };

  const handleDeleteEntry = (slotId, entryId) => {
    setDeleteSlotId(slotId);
    setDeleteEntryId(entryId);
    setDeleteModalVisible(true);
  };

    const loadWeekData = useCallback(async (page = 1, phaseParam = null) => {
    if (!apiClient) return;
    // 如果 babyId 不存在，则不发送请求
    if (!babyId) {
      setScheduleRows([]);
      setTotalRecords(0);
      return;
    }
    setPageLoading(true);
    try {
      const payload = {
        // coach: coach,
        pageNo: page,
        pageSize: pageSize,
        courseKbn: activeTab === 'offline' ? '线下课程' : '家庭训练',
        babyId: babyId, // 必须包含babyId参数
      };
      
      // 如果传入了phase参数或者组件内部有phase状态，则添加到请求参数中
      const effectivePhase = phaseParam !== null && phaseParam !== undefined ? phaseParam : phase;
      if (effectivePhase !== undefined && effectivePhase !== null) {
        payload.phase = effectivePhase;
      }
      
      console.log('发送请求参数:', payload);
      
      const resp = await apiClient.post('/sports-server-prod/taller.api.v1/prescription/pageList', payload);
      console.log('后台响应数据:', resp?.data);
      
      if (resp?.data?.code !== 200) {
        throw new Error(resp?.data?.msg || '获取排课数据失败');
      }
      const list = resp.data.data?.list || [];
      const total = resp.data.data?.totalRecord || 0;
      
      console.log('解析后的数据:', { list, total, page });
      
      // 直接使用返回的数据作为分页列表
      setScheduleRows(list);
      setTotalRecords(total);
      // 设置默认页码：只有在没有外部phase控制时才设置为最大阶段
      // 如果有phase参数传入，则使用传入的phase值
      const totalPages = Math.ceil(total / pageSize);
      if (phase === undefined || phase === null) {
        setCurrentPage(total > 0 ? totalPages : 1);
      }
      
      // 通知父组件数据加载完成
      if (onDataLoad) {
        onDataLoad({ totalRecords: total, pageSize: pageSize });
      }
      
      console.log('设置后的状态:', { scheduleRows: list, totalRecords: total, currentPage: page });
    } catch (e) {
      console.error('加载数据出错:', e);
      message.error(e?.message || '获取排课数据失败');
    } finally {
      setPageLoading(false);
    }
    }, [apiClient, coach, activeTab, babyId, pageSize]);

  // 监听phase参数变化，自动加载对应阶段数据
  useEffect(() => {
    if (phase !== undefined && phase !== null && babyId) {
      const targetPage = Number(phase);
      setCurrentPage(targetPage);
      loadWeekData(targetPage);
    }
  }, [phase, babyId, loadWeekData]);

  const handleSave = async () => {
    if (!apiClient) return;
    // 构建保存任务 - 按会员和时间分组
    const tasks = [];
    Object.keys(scheduleRows).forEach((dateKey) => {
      const rows = scheduleRows[dateKey];
      Object.values(rows).forEach((row) => {
        if (!row || !Array.isArray(row.entries) || !row.entries.length) return;
        const period = row.period || DEFAULT_CONSUME;
        row.entries.forEach((entry) => {
          const { members = [], courses = [] } = entry;
          if (!members.length || !courses.length) return;
          
          // 按会员分组课程
          members.forEach((member) => {
            // 查找或创建该会员在此时间段的任务
            let task = tasks.find(t => t.member.id === member.id && t.dateTime.isSame(row.startTime, 'minute'));
            if (!task) {
              task = {
                member,
                dateTime: row.startTime,
                period,
                courses: [],
                entryId: entry.id,
              };
              tasks.push(task);
            }
            // 添加课程到该任务
            courses.forEach(course => {
              if (!task.courses.some(c => 
                (c.seq && c.seq === course.seq) || 
                (c._key && c._key === course._key)
              )) {
                task.courses.push(course);
              }
            });
          });
        });
      });
    });

    if (!tasks.length) {
      message.info('当前没有可保存的排课');
      return;
    }

    const total = tasks.length;
    let completed = 0;
    const errors = [];

    setSaving(true);
    setSaveProgress({ completed: 0, total, currentMemberName: '' });

    const creator = user?.name || '';

    for (const task of tasks) {
      const memberName = task.member.babyName || '';
      const payload = {
        ...buildMemberPayload({
          member: task.member,
          course: null, // 不在这里设置单个课程，因为课程会在details中设置
          dateTime: task.dateTime,
          period: task.period,
          creator,
          coach: coach,
          id: task.entryId,
        }),
        // 用任务中的课程列表替换details
        details: task.courses.map((course) => ({
          sportsItem: course.eventTitle,
          sportsContent: course.courseContext || '',
        })),
        // 添加课程区分参数
        courseKbn: activeTab === 'offline' ? '线下课程' : '家庭训练',
      };
      
      try {
        const resp = await apiClient.post('/sports-server-prod/taller.api.v1/prescription/add', payload);

        if (!(resp?.data && resp.data.code === 200)) {
          throw new Error(resp?.data?.msg || '保存失败');
        }
      } catch (e) {
        errors.push({
          memberName: task.member.babyName,
          time: task.dateTime.format('YYYY-MM-DD HH:mm'),
          courseTitle: task.courses.map(c => c.eventTitle).join(','),
          msg: e?.message || '保存失败',
        });
      } finally {
        completed += 1;
        setSaveProgress({ completed, total, currentMemberName: memberName });
      }
    }

    setSaving(false);
    if (errors.length) {
      const brief = errors.slice(0, 5).map((er) => `${er.time} ${er.memberName} ${er.courseTitle || ''}: ${er.msg}`).join('\n');
      message.error(`部分排课保存失败，共 ${errors.length} 条。前5条如下：\n${brief}`);
    } else {
      message.success('排课保存成功');
      // 重新加载本周数据
      loadWeekData();
    }
  };

  const handleCopySchedule = () => {
    if (!copyTargetDate) {
      message.warning('请选择目标日期');
      return;
    }
    const sourceRows = scheduleRows[currentDate.format(formatDate)] || {};
    const targetKey = copyTargetDate.format(formatDate);
    let targetRows = { ...scheduleRows[targetKey] } || {};
    if (copyMode === 'overwrite') {
      targetRows = { ...sourceRows };
    } else {
      // merge
      Object.keys(sourceRows).forEach(slotId => {
        if (!targetRows[slotId]) {
          targetRows[slotId] = { ...sourceRows[slotId] };
        } else {
          // for simplicity, overwrite the row
          targetRows[slotId] = { ...sourceRows[slotId] };
        }
      });
    }
    // adjust startTime for target date
    Object.keys(targetRows).forEach(slotId => {
      const row = targetRows[slotId];
      const [hour, minute] = slotId.split(':').map(Number);
      row.startTime = copyTargetDate.hour(hour).minute(minute).second(0);
    });
    setScheduleRows(prev => ({ ...prev, [targetKey]: targetRows }));
    message.success('排课复制成功');
    setCopyModalVisible(false);
  };

  // 复制课程函数 - 参考MemberList.js的接口
  const handleCopyCourse = async (slotId) => {
    const row = currentRows[slotId];
    if (!row || !row.entries || row.entries.length === 0) return;
    
    const firstEntry = row.entries[0];
    if (!firstEntry || !firstEntry.members || firstEntry.members.length !== 1) return;
    
    const member = firstEntry.members[0];
    
    try {
      // 首先获取最近的课程历史
      const resp = await apiClient.get('/sports-server-prod/taller.api.v1/prescriptionDetail/getlistByBabyId', {
        params: { babyId: member.id }
      });

      if (resp.data && resp.data.code === 200) {
        const list = resp.data.data?.list || [];
        if (list.length > 0) {
          // 获取最新的课程记录
          const latestCourse = list[0]; // 假设列表按时间倒序排列
          const newCourses = [{
            _key: `${latestCourse.sportsItem || ''}-${latestCourse.sportsContent || ''}`,
            courseKbn: latestCourse.sportsItem,
            eventTitle: latestCourse.sportsItem?.split('/')[1] || latestCourse.sportsItem,
            courseContext: latestCourse.sportsContent,
          }];
          
          // 更新当前行的课程
          setScheduleRows((prev) => {
            const dateKey = currentDate.format(formatDate);
            const updatedRows = { ...prev[dateKey] };
            const updatedSlotId = slotId;
            let updatedRow = updatedRows[updatedSlotId];
            if (!updatedRow) return;
            
            if (!updatedRow.entries.length) {
              updatedRow.entries.push({
                id: `entry-${updatedSlotId}-0`,
                members: [...firstEntry.members],
                courses: [],
                isExisting: false,
                updateFlag: 0,
              });
            }
            updatedRow.entries[0].courses = newCourses;
            updatedRow.entries[0].updateFlag = 1;
            return { ...prev, [dateKey]: updatedRows };
          });
          
          message.success('课程复制成功');
        } else {
          message.info('该会员暂无历史课程记录');
        }
      } else {
        message.error(resp.data?.msg || '获取历史课程失败');
      }
    } catch (e) {
      console.error('复制课程失败:', e);
      message.error('复制课程失败');
    } finally {
      setCopyCourseModal(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteEntryId) return;
    if (typeof deleteEntryId === 'number') {
      try {
        const resp = await apiClient.post('/sports-server-prod/taller.api.v1/prescription/delete?id=' + deleteEntryId);
        if (resp?.data?.code === 200) {
          message.success('删除成功');
          // 重新加载当前页数据
          loadWeekData(currentPage);
        } else {
          message.error(resp?.data?.msg || '删除失败');
          return;
        }
      } catch (e) {
        message.error('删除失败');
        return;
      }
    }
    setDeleteModalVisible(false);
  };

    const handleRemoveMemberConfirm = async () => {
    if (!removeMemberSlotId || !removeMemberId) return;
    
    const row = currentRows[removeMemberSlotId];
    if (row) {
      const entry = row.entries.find(e => e.members.some(m => m.id === removeMemberId));
      
      // 如果entry有ID（表示已保存到数据库），则调用API删除
      if (entry && typeof entry.id === 'number') {
        try {
          const resp = await apiClient.post('/sports-server-prod/taller.api.v1/prescription/delete?id=' + entry.id);
          if (resp?.data?.code === 200) {
            message.success('删除成功');
          } else {
            message.error(resp?.data?.msg || '删除失败');
            return;
          }
        } catch (e) {
          message.error('删除失败');
          return;
        }
      }
      // 如果entry没有ID（表示只是临时添加的记录），则跳过API调用，只更新本地状态
    }
    
    // 从本地状态移除会员
    setScheduleRows((prev) => {
      const dateKey = currentDate.format(formatDate);
      const updatedRows = { ...prev[dateKey] };
      const row = updatedRows[removeMemberSlotId];
      if (!row) return prev;
      
      row.entries.forEach((entry) => {
        entry.members = (entry.members || []).filter((m) => m.id !== removeMemberId);
      });
      
      // 清理空的entry
      const filteredEntries = row.entries.filter(entry => (entry.members || []).length > 0);
      if (filteredEntries.length === 0) {
        delete updatedRows[removeMemberSlotId];
      } else {
        row.entries = filteredEntries;
      }
      
      return { ...prev, [dateKey]: updatedRows };
    });
    
    setRemoveMemberModalVisible(false);
    message.success('移除会员成功');
  };

  const handleSingleSave = (slotId, entryId) => {
    // 在新的表格视图中，slotId可能为null
    setSingleSaveSlotId(slotId || 'table-view');
    setSingleSaveEntryId(entryId);
    setSingleSaveModalVisible(true);
  };

  const handleSingleSaveConfirm = async () => {
    if (!singleSaveEntryId) return;
    
    // 关键修复：根据实际数据结构查找记录
    // scheduleRows现在是一个数组，直接在数组中查找
    let record = scheduleRows.find(r => r.id === singleSaveEntryId);
    
    if (!record) {
      message.error(`未找到要保存的记录 (ID: ${singleSaveEntryId})`);
      return;
    }

    const creator = user?.name || '';
    
    // 构建保存的payload
    const payload = {
      accClasses: 0,
      babyId: record.babyId,
      babyName: record.babyName,
      briefSummary: record.briefSummary || '',
      coach: coach,
      completion: record.completion || '',
      consumeClass: record.consumeClass || record.trainTimes || DEFAULT_CONSUME,
      // 关键修复：使用原始记录的createUser和updateUser
      createUser: record.createUser || creator,
      customerName: record.customerName || '',
      details: Array.isArray(record.details) ? record.details : [],
      heartRate: record.heartRate || '',
      id: record.id,
      points: record.points || '',
      restTimes: record.restTimes || '',
      suggestion: record.suggestion || '',
      // 关键修复：使用记录中更新后的trainDate
      trainDate: record.trainDate,
      trainTimes: record.trainTimes || DEFAULT_CONSUME,
      // 关键修复：使用原始记录的updateUser
      updateUser: record.updateUser || creator,
      teachingMode: record.teachingMode || '一对一',
      courseKbn: activeTab === 'offline' ? '线下课程' : '家庭训练',
    };

    try {
      const resp = await apiClient.post('/sports-server-prod/taller.api.v1/prescription/add', payload);
      if (resp?.data && resp.data.code === 200) {
        message.success('保存成功');
        // 重新加载当前页数据
        loadWeekData(currentPage, phase);
      } else {
        throw new Error(resp?.data?.msg || '保存失败');
      }
    } catch (e) {
      message.error(e?.message || '保存失败');
    }
    
    setSingleSaveModalVisible(false);
  };

  // 处理变更时间 - 按照ScheduCourse.js的逻辑
  const handleChangeTime = (entryId) => {
    console.log('handleChangeTime called with entryId:', entryId);
    console.log('Current scheduleRows:', scheduleRows);
    
    setChangeTimeEntryId(entryId);
    
    // 从选中的记录中获取当前的日期和时间
    // scheduleRows现在是一个数组，直接在数组中查找
    const selectedRecord = scheduleRows.find(r => r.id === entryId);
    
    console.log('Found selectedRecord:', selectedRecord);
    
    let defaultDate = currentDate;
    let defaultTime = timeSlots[0]?.startTime || dayjs().hour(9).minute(0);
    
    // 如果找到记录，使用记录中的时间信息
    if (selectedRecord && selectedRecord.trainDate) {
      const recordDateTime = dayjs(selectedRecord.trainDate);
      defaultDate = recordDateTime.startOf('day');
      defaultTime = recordDateTime;
    }
    
    setSelectedNewDate(defaultDate);
    setSelectedNewTime(defaultTime);
    
    setChangeTimeModalVisible(true);
  };

  // 确认变更时间 - 按照ScheduCourse.js的逻辑
  const handleChangeTimeConfirm = async () => {
    console.log('handleChangeTimeConfirm called');
    console.log('selectedNewDate:', selectedNewDate);
    console.log('selectedNewTime:', selectedNewTime);
    console.log('changeTimeEntryId:', changeTimeEntryId);
    
    if (!selectedNewDate || !selectedNewTime) {
      message.warning('请选择新的日期和时间');
      return;
    }

    // 创建新的时间信息
    const newDateTime = selectedNewDate.hour(selectedNewTime.hour()).minute(selectedNewTime.minute());
    
    console.log('newDateTime:', newDateTime.format('YYYY-MM-DD HH:mm'));
    
    // 更新排课数据中的时间
    setScheduleRows((prev) => {
      console.log('Updating scheduleRows, current state:', prev);
      
      // 找到需要更新的记录
      const recordIndex = prev.findIndex(r => r.id === changeTimeEntryId);
      console.log('Found recordIndex:', recordIndex);
      
      if (recordIndex === -1) {
        console.log('Record not found, returning original state');
        return prev;
      }
      
      // 获取记录对象
      const record = prev[recordIndex];
      console.log('Found record to update:', record);
      
      // 更新记录的时间信息
      const updatedRecord = { ...record };
      updatedRecord.updateFlag = 1; // 标记为需要更新
      // 关键修复：更新trainDate字段
      updatedRecord.trainDate = newDateTime.format('YYYY-MM-DD HH:mm');
      updatedRecord.trainTimes = record.trainTimes || DEFAULT_CONSUME;
      
      console.log('Updated record:', updatedRecord);
      
      // 创建新的数组并替换原记录
      const updatedRecords = [...prev];
      updatedRecords[recordIndex] = updatedRecord;
      
      console.log('New scheduleRows state:', updatedRecords);
      
      return updatedRecords;
    });
    
    // 调用保存函数
    console.log('变更时间完成，准备保存:', {
      changeTimeEntryId,
      newDateTime: newDateTime.format('YYYY-MM-DD HH:mm')
    });
    
    // 等待状态更新完成后再调用保存
    setTimeout(async () => {
      try {
        await handleSingleSave(null, changeTimeEntryId);
      } catch (error) {
        console.error('保存变更后的时间失败:', error);
        message.error('时间变更成功，但保存失败');
      }
    }, 100);
    
    setChangeTimeModalVisible(false);
  };

  // 处理AI教练生成计划
  const handleGenerateAIPlan = async (recordIndex) => {
    // 设置防重复点击状态
    setAiCoachLoading(prev => ({ ...prev, [recordIndex]: true }));
    
    try {
      // 获取对应的记录
      const record = (currentRows || [])[recordIndex];
      if (!record) {
        message.error('未找到对应的排课记录');
        return;
      }
      
      // 根据项目规范，获取会员ID采用三级优先级
      let memberId = null;
      let memberInfo = null;
      
      // 1. 优先使用记录中的babyId
      if (record.babyId) {
        memberId = record.babyId;
        memberInfo = {
          id: record.babyId,
          babyName: record.babyName,
          customerId: record.customerId
        };
      }
      
      // 2. 其次使用行内成员信息中的id
      if (!memberId && record.entries && record.entries[0] && record.entries[0].members && record.entries[0].members.length > 0) {
        const member = record.entries[0].members[0];
        memberId = member.id;
        memberInfo = member;
      }
      
      // 3. 最后使用组件入参传入的babyId
      if (!memberId && babyId) {
        memberId = babyId;
        memberInfo = {
          id: babyId,
          babyName: memberInfo?.babyName || '未知会员',
          customerId: storedUser.customerId ? storedUser.customerId.split('-')[0] : ''
        };
      }
      
      // 如果仍然没有获取到会员ID，则报错
      if (!memberId) {
        message.error('未找到会员信息，请检查组件参数配置');
        return;
      }
      
      // 确保memberInfo存在
      if (!memberInfo) {
        memberInfo = {
          id: memberId,
          babyName: '未知会员',
          customerId: storedUser.customerId ? storedUser.customerId.split('-')[0] : ''
        };
      }
      
      // 准备请求参数
      const customerId = memberInfo.customerId || (storedUser.customerId ? storedUser.customerId.split('-')[0] : '');
      const payload = {
        customerId: customerId, // 当前会员的机构ID
        babyId: memberId, // 当前会员id
        trainDate: dayjs(record.trainDate).format('YYYY-MM-DD') // 当前训练日
      };
      
      // 调用后端接口
      const response = await apiClient.post('/sports-server-prod/taller.api.v1/aicoach/generatePlan', payload);
      
      if (response.data && response.data.code === 200) {
        const planData = response.data.data;
        
        if (!planData || planData.trim() === '') {
          message.info('未匹配到合适计划，请继续学习');
          return;
        }
        
        // 解析返回的数据
        const planItems = planData.split(';').filter(item => item.trim() !== '');
        
        const courses = planItems.map((item, index) => {
          const parts = item.split('-');
          return {
            _key: `${parts[0] || `AI课程${index+1}`}-${parts[1] || ''}`,
            eventTitle: parts[0] || `AI课程${index+1}`,
            courseContext: parts.length > 1 ? parts[1] : '',
          };
        });
        
        // 更新当前记录的课程
        setScheduleRows((prev) => {
          // 找到对应的记录并更新
          const recordToUpdateIndex = prev.findIndex((r, index) => index === recordIndex);
          if (recordToUpdateIndex === -1) {
            return prev;
          }
          
          const recordToUpdate = { ...prev[recordToUpdateIndex] };
          
          // 确保entries存在
          if (!recordToUpdate.entries) {
            recordToUpdate.entries = [];
          }
          
          // 确保至少有一个entry
          if (recordToUpdate.entries.length === 0) {
            recordToUpdate.entries.push({
              id: recordToUpdate.id,
              members: [memberInfo],
              courses: courses,
              isExisting: true,
              updateFlag: 1,
            });
          } else {
            // 更新第一个entry
            recordToUpdate.entries[0].courses = courses;
            recordToUpdate.entries[0].updateFlag = 1;
            // 确保会员信息也正确设置
            if (!recordToUpdate.entries[0].members || recordToUpdate.entries[0].members.length === 0) {
              recordToUpdate.entries[0].members = [memberInfo];
            }
          }
          
          // 同时更新details字段以保持数据一致性
          recordToUpdate.details = courses.map(course => ({
            sportsItem: course.eventTitle,
            sportsContent: course.courseContext
          }));
          
          // 创建新数组并更新记录
          const updatedRecords = [...prev];
          updatedRecords[recordToUpdateIndex] = recordToUpdate;
          
          return updatedRecords;
        });
        
        message.success('AI已为您制定训练计划');
      } else {
        message.error(response.data?.msg || '生成计划失败');
      }
    } catch (error) {
      console.error('AI教练生成计划失败:', error);
      message.error(error.response?.data?.msg || '生成计划失败');
    } finally {
      // 重置防重复点击状态
      setAiCoachLoading(prev => {
        const newLoading = { ...prev };
        delete newLoading[recordIndex];
        return newLoading;
      });
    }
  };

  const tabItems = [
    {
      key: 'offline',
      label: '线下课程',
    },
    {
      key: 'home',
      label: '家庭训练',
    },
  ];

  return (
    <div className="schedule-course">
      <Card style={{ marginBottom: 16 }}>
        <Row align="middle" gutter={[4, 0]}>
          <Col xs={24} md={9}>
            {/* 阶段信息展示 - 由TrainingPlan.js控制 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontWeight: 'bold', fontSize: '16px' }}>
                第 {currentPage} 阶段训练计划
              </span>
            </div>
          </Col>
          <Col xs={24} md={15} style={{ textAlign: 'right' }}>
            <Space size={8}>
              <Button type="default" icon={<SearchOutlined />} onClick={() => loadWeekData(phase || 1, phase)}>
                刷新
              </Button>
              <Button onClick={handlePrintSchedule} loading={printing}>
                打印排课表
              </Button>
              <Button type="primary" icon={<RobotOutlined />} onClick={handleOpenAiCoachModal} loading={aiCoachPreparing}>
                AI教练
              </Button>
             </Space>
           </Col>
         </Row>
         <Tabs activeKey={activeTab} onChange={handleTabChange} items={tabItems} tabBarGutter={16} style={{ marginTop: 16 }} />
         {saving && (
           <Row style={{ marginTop: 16 }}>
             <Col span={24}>
               <Space direction="vertical" style={{ width: '100%' }}>
                 <Text>
                   正在保存排课：{saveProgress.currentMemberName} ({saveProgress.completed}/{saveProgress.total})
                 </Text>
                 <Progress percent={Math.round((saveProgress.completed / (saveProgress.total || 1)) * 100)} />
               </Space>
             </Col>
           </Row>
         )}
       </Card>

       <Card ref={schedulePrintRef}>
         {pageLoading ? (
           <Spin />
         ) : (
           <div>
             {/* 分页表格 */}
             <div style={{ minHeight: 600 }}>
               {/* 只渲染实际存在的数据行 */}
               {currentRows.length > 0 ? (
                 currentRows.map((record, index) => {
                   const actualIndex = (currentPage - 1) * pageSize + index;
                   
                   // 调试信息
                   if (index === 0) {
                     console.log('渲染调试:', {
                       currentPage,
                       pageSize,
                       totalRecords,
                       currentRowsLength: currentRows.length,
                       actualIndexRange: `${(currentPage - 1) * pageSize} - ${(currentPage - 1) * pageSize + currentRows.length - 1}`
                     });
                   }
                   
                   return (
                     <Row
                       key={index}
                       align="middle"
                       style={{
                         borderBottom: '1px solid #f0f0f0',
                         padding: '12px 0',
                         minHeight: '60px',
                       }}
                       gutter={16}
                     >
                       {/* 序号列 */}
                       <Col span={1}>
                         <Text strong>{actualIndex + 1}</Text>
                       </Col>
                       
                       {/* 训练日列 */}
                       <Col span={3}>
                         {record ? (
                           <Space direction="vertical" size={4}>
                             <Text strong>{dayjs(record.trainDate).format('YYYY-MM-DD HH:mm')}</Text>
                             {typeof record.id === 'number' && record.completeClass !== '1' && (
                               <Button type="default" size="small" onClick={() => handleChangeTime(record.id)}>
                                 变更时间
                               </Button>
                             )}
                           </Space>
                         ) : (
                           <Text type="secondary"></Text>
                         )}
                       </Col>
                       
                       {/* 状态列 */}
                       <Col span={2}>
                         {record ? (
                           <div style={{ textAlign: 'center' }}>
                             {record.completeClass === '1' ? (
                               <Badge status="success" text="已完成" />
                             ) : typeof record.id === 'number' && record.completeClass !== '1' ? (
                               <Badge status="processing" text="进行中" />
                             ) : (
                               <Badge status="default" text="待排课" />
                             )}
                           </div>
                         ) : (
                           <Text type="secondary"></Text>
                         )}
                       </Col>
                       
                       {/* 动作内容列 */}
                       <Col span={11}>
                         {record ? (
                           <Card
                             size="small"
                             style={{ minWidth: 300 }}
                             bodyStyle={{ padding: 12 }}
                           >
                             <Space direction="vertical" size={6} style={{ width: '100%' }}>
                               <div>
                                 <Text type="secondary">动作内容：</Text>
                                 {record.entries && record.entries[0] && record.entries[0].courses && record.entries[0].courses.length > 0 ? (
                                   record.entries[0].courses.map((course, idx) => (
                                     <Tag key={idx}>{course.eventTitle}</Tag>
                                   ))
                                 ) : Array.isArray(record.details) && record.details.length > 0 ? (
                                   record.details.map((detail, idx) => (
                                     <Tag key={idx}>{detail.sportsItem}</Tag>
                                   ))
                                 ) : (
                                   <Text type="secondary">未选择</Text>
                                 )}
                               </div>
                               
                               <Space>
                                 {record.completeClass !== '1' && typeof record.id === 'number' && (
                                   <Button type="link" size="small" onClick={() => handleOpenTrainingFeedback({
                                     ...record,
                                     members: [{
                                       id: record.babyId,
                                       babyName: record.babyName,
                                       deliveryType: record.teachingMode,
                                       customerId: record.customerId
                                     }],
                                     entries: [{
                                       id: record.id,
                                       members: [{
                                         id: record.babyId,
                                         babyName: record.babyName,
                                         deliveryType: record.teachingMode
                                       }],
                                       courses: Array.isArray(record.details) ? record.details.map(d => ({
                                         eventTitle: d.sportsItem,
                                         courseContext: d.sportsContent
                                       })) : []
                                     }]
                                   })}>
                                     修改
                                   </Button>
                                 )}
                               </Space>
                             </Space>
                           </Card>
                         ) : (
                           <Text type="secondary">暂无排课</Text>
                         )}
                       </Col>
                       
                       {/* 操作列 */}
                       <Col span={5} style={{ display: 'flex', alignItems: 'center' }}>
                         {record ? (
                           <Space direction="horizontal" size={8} style={{ width: '100%' }}>
                             {(record.completeClass !== '1' || 
                               (record.entries && record.entries[0] && record.entries[0].updateFlag === 1)) && (
                               <Button type="primary" size="small" onClick={() => handleSingleSave(null, record.id)}>
                                 保存
                               </Button>
                             )}
                             
                             {record.completeClass !== '1' && typeof record.id === 'number' && (
                               <Button 
                                 type="default" 
                                 size="small" 
                                 icon={<RobotOutlined />}
                                 loading={aiCoachLoading[actualIndex]}
                                 onClick={() => handleGenerateAIPlan(actualIndex)}
                               >
                                 AI教练
                               </Button>
                             )}
                             
                             {typeof record.id === 'number' && (
                               <Button type="default" size="small" onClick={() => handleOpenTrainingFeedback({
                                 ...record,
                                 members: [{
                                   id: record.babyId,
                                   babyName: record.babyName,
                                   deliveryType: record.teachingMode,
                                   customerId: record.customerId
                                 }],
                                 entries: [{
                                   id: record.id,
                                   members: [{
                                     id: record.babyId,
                                     babyName: record.babyName,
                                     deliveryType: record.teachingMode
                                   }],
                                   courses: Array.isArray(record.details) ? record.details.map(d => ({
                                     eventTitle: d.sportsItem,
                                     courseContext: d.sportsContent
                                   })) : []
                                 }]
                               })}>
                                 训练反馈
                               </Button>
                             )}
                             
                             {record.completeClass !== '1' && (
                               <Button 
                                 type="default" 
                                 size="small" 
                                 danger 
                                 onClick={() => {
                                   setDeleteEntryId(record.id);
                                   setDeleteModalVisible(true);
                                 }}
                               >
                                 删除
                               </Button>
                             )}
                           </Space>
                         ) : (
                           <Space size="middle">
                             <Tooltip title="添加排课">
                               <Button
                                 type="primary"
                                 icon={<UserAddOutlined />}
                                 onClick={() => {
                                   // 这里可以打开添加排课的模态框
                                   message.info('请通过上方的添加按钮来创建新的排课');
                                 }}
                               >
                                 添加排课
                               </Button>
                             </Tooltip>
                           </Space>
                         )}
                       </Col>
                     </Row>
                   );
                 })
               ) : (
                 // 当没有数据时显示提示
                 <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                   <Text>暂无排课数据</Text>
                 </div>
               )}
             </div>
             

           </div>
         )}
       </Card>

       <Drawer
         title={activeSelectorType === 'member' ? '选择会员' : activeSelectorType === 'course' ? '从动作资源库选择' : ''}
         placement="right"
         width={480}
         onClose={closeDrawer}
         open={!!activeSelectorType}
       >
         {activeSelectorType === 'member' && activeRowSlotId && (
           <MemberSelector
             onSelect={handleMemberSelect}
             selectedMemberIds={getRowMembers(activeRowSlotId)}
           />
         )}
         {activeSelectorType === 'course' && activeRowSlotId && (
           <CourseSelector
             onCoursesSelect={handleCourseSelectFromLibrary}
             selectedCourseSeqs={(currentRows[activeRowSlotId]?.entries?.[0]?.courses || []).map((c) => c.seq).filter(Boolean)}
             defaultCourseType={activeTab === 'offline' ? '线下课程' : '家庭训练'}
             disabled={false}
           />
         )}
       </Drawer>

       {/* 我的课程选择器模态框 */}
       <MyCourseSelector
         open={myCourseSelectorModalVisible}
         onCancel={() => setMyCourseSelectorModalVisible(false)}
         onCourseSelect={handleMyCourseSelect}
       />

       <Modal
         title="复制排课"
         open={copyModalVisible}
         onCancel={() => setCopyModalVisible(false)}
         onOk={handleCopySchedule}
         okText="复制"
         cancelText="取消"
       >
         <Space direction="vertical">
           <div>
             <Text>选择目标日期：</Text>
             <Select
               style={{ width: 120 }}
               value={copyTargetDate ? copyTargetDate.format(formatDate) : undefined}
               onChange={(value) => setCopyTargetDate(dayjs(value))}
             >
               {weekDays.filter((d) => !d.date.isSame(currentDate, 'day')).map((d) => (
                 <Select.Option key={d.date.format(formatDate)} value={d.date.format(formatDate)}>
                   {d.date.format('MM-DD')} {d.label}
                 </Select.Option>
               ))}
             </Select>
           </div>
           <Radio.Group value={copyMode} onChange={(e) => setCopyMode(e.target.value)}>
             <Radio value="overwrite">覆盖</Radio>
             <Radio value="merge">合并</Radio>
           </Radio.Group>
         </Space>
       </Modal>
       
       <Modal
         title="确认复制课程"
         open={copyCourseModal}
         onCancel={() => setCopyCourseModal(false)}
         footer={[
           <Button key="cancel" onClick={() => setCopyCourseModal(false)}>
             取消
           </Button>,
           <Button key="confirm" type="primary" onClick={() => handleCopyCourse(copySlotId)}>
             确认复制
           </Button>,
         ]}
       >
         <p>是否确认复制该会员最近的课程到当前时间段？</p>
       </Modal>

       <Modal
         title="删除确认"
         open={deleteModalVisible}
         onCancel={() => setDeleteModalVisible(false)}
         footer={[
           <Button key="cancel" onClick={() => setDeleteModalVisible(false)}>
             取消
           </Button>,
           <Button key="confirm" type="primary" danger onClick={handleDeleteConfirm}>
             确认删除
           </Button>,
         ]}
       >
         <p>确定要删除该时间段的排课吗？</p>
       </Modal>

       <Modal
         title="移除会员确认"
         open={removeMemberModalVisible}
         onCancel={() => setRemoveMemberModalVisible(false)}
         footer={[
           <Button key="cancel" onClick={() => setRemoveMemberModalVisible(false)}>
             取消
           </Button>,
           <Button key="confirm" type="primary" onClick={handleRemoveMemberConfirm}>
             确认移除
           </Button>,
         ]}
       >
         <p>确定要移除该会员吗？</p>
       </Modal>

       <Modal
         title="保存确认"
         open={singleSaveModalVisible}
         onCancel={() => setSingleSaveModalVisible(false)}
         footer={[
           <Button key="cancel" onClick={() => setSingleSaveModalVisible(false)}>
             取消
           </Button>,
           <Button key="confirm" type="primary" onClick={handleSingleSaveConfirm}>
             确认保存
           </Button>,
         ]}
       >
         <p>确定要保存该明细吗？</p>
       </Modal>

       {/* AI教练排课模态框 */}
       <Modal
         title="AI教练排课"
         open={aiCoachModalVisible}
         onCancel={handleCloseAiCoachModal}
         width={1000}
         footer={[
           <Button key="cancel" onClick={handleCloseAiCoachModal} disabled={aiCoachModalSaving}>
             取消
           </Button>,
           <Button key="save" type="primary" loading={aiCoachModalSaving} onClick={handleConfirmAiCoachSave}>
             保存
           </Button>,
         ]}
       >
         <Space direction="vertical" style={{ width: '100%' }} size={12}>
           <Row gutter={12} style={{ fontWeight: 600 }}>
             <Col span={6}>训练日</Col>
             <Col span={4}>时刻</Col>
             <Col span={12}>动作内容</Col>
             <Col span={2}>操作</Col>
           </Row>
           {aiCoachRows.length ? (
             aiCoachRows.map((row, index) => (
               <Row key={row.key} gutter={12} align="middle" style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: 8, marginBottom: 8 }}>
                 <Col span={6}>
                   <DatePicker
                     value={row.trainDate}
                     onChange={(date) => handleAiCoachRowFieldChange(row.key, 'trainDate', date)}
                     style={{ width: '100%' }}
                     placeholder="请选择训练日"
                     format="YYYY-MM-DD"
                   />
                 </Col>
                 <Col span={4}>
                   <Select
                     placeholder="请选择时刻"
                     value={row.trainTime || undefined}
                     onChange={(value) => handleAiCoachRowFieldChange(row.key, 'trainTime', value)}
                     options={aiCoachTimeOptions}
                   />
                 </Col>
                 <Col span={12}>
                   {/* 动作内容区域 - 使用Tag展示sportsItem，隐藏sportsContent */}
                   <div style={{ minHeight: 40, border: '1px solid #d9d9d9', borderRadius: 4, padding: 8, backgroundColor: '#fafafa' }}>
                     {row.details && row.details.length > 0 ? (
                       <Space wrap>
                         {row.details.map((detail, detailIndex) => (
                           <Tag 
                             key={detailIndex}
                             closable
                             onClose={() => handleAiCoachDetailDelete(row.key, detailIndex)}
                             style={{ margin: '2px 4px' }}
                           >
                             {detail.sportsItem}
                           </Tag>
                         ))}
                       </Space>
                     ) : (
                       <Text type="secondary">暂无动作内容</Text>
                     )}
                     {/* 隐藏的sportsContent字段 */}
                     <div style={{ display: 'none' }}>
                       {row.details && row.details.map((detail, detailIndex) => (
                         <input 
                           key={detailIndex}
                           type="hidden" 
                           value={detail.sportsContent || ''} 
                         />
                       ))}
                     </div>
                   </div>
                 </Col>
                 <Col span={2} style={{ textAlign: 'center' }}>
                   <Button danger type="link" onClick={() => handleAiCoachRowDelete(row.key)}>
                     删除
                   </Button>
                 </Col>
               </Row>
             ))
           ) : (
             <Text type="secondary">暂无AI教练生成的明细</Text>
           )}
         </Space>
       </Modal>

       {/* 训练反馈弹窗 */}
       {currentTrainingRecord && (
         <PostClassFeedback
           currentScheduleMember={currentTrainingRecord.members?.[0] || null}
           totalRecord={1}
           completeClass={currentTrainingRecord.completeClass || ''}
           setFinishConfirmVisible={() => {}}
           loading={false}
           scheduleModalVisible={trainingFeedbackModalVisible}
           setScheduleModalVisible={setTrainingFeedbackModalVisible}
           summaryViewMode={true}
           setSummaryViewMode={() => {}}
           suggestionViewMode={true}
           setSuggestionViewMode={() => {}}
           normalizeRichText={(raw) => raw}
           scheduleForm={trainingFeedbackForm}
           summaryRef={null}
           suggestionRef={null}
           handleCopyPostClassHistory={() => {}}
           sportsOptions={[]}
           visibleTabs={['1', '2']}
           handleDateChange={() => {}}
           scheduleRef={trainingFeedbackScheduleRef}
           prescriptionId={typeof currentTrainingRecord.id === 'number' ? currentTrainingRecord.id : null}
         />
       )}

       {/* 未来排课弹窗 */}
       <Modal
         title="未来排课"
         open={futureSchedulingModalVisible}
         onCancel={() => setFutureSchedulingModalVisible(false)}
         footer={[
           <Button key="cancel" onClick={() => setFutureSchedulingModalVisible(false)}>
             取消
           </Button>,
           <Button key="confirm" type="primary" onClick={handleConfirmScheduling}>
             确认排课
           </Button>,
         ]}
       >
         <div>
           <p style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
             <span>请选择要排课的日期（已排课日期不可选）：</span>
             <Button 
               type="text" 
               onClick={() => setDatePickerOpen(false)}
               style={{ padding: '0', fontSize: '16px' }}
             >
               ×
             </Button>
           </p>
           <div style={{ maxHeight: '400px', overflow: 'auto', border: '1px solid #d9d9d9', borderRadius: 6, padding: '8px' }}>
             <DatePicker
               picker="date"
               value={null}
               open={datePickerOpen}
               onOpenChange={setDatePickerOpen}
               disabledDate={(date) => {
                 const today = dayjs().startOf('day');
                 const oneMonthLater = today.add(1, 'month');
                 // 确保格式一致进行比较
                 const formattedDate = date.format('YYYY-MM-DD');
                 // 如果日期已在已选列表中或在禁用列表中，则禁用
                 const isSelected = selectedDates.some(d => d.format('YYYY-MM-DD') === formattedDate);
                 return date.isBefore(today, 'day') || date.isAfter(oneMonthLater, 'day') || disabledDates.includes(formattedDate) || isSelected;
               }}
               onChange={(date) => {
                 if (date) {
                   const formattedDate = date.format('YYYY-MM-DD');
                   // 检查是否已选择该日期
                   const isSelected = selectedDates.some(d => d.format('YYYY-MM-DD') === formattedDate);
                   if (!isSelected) {
                     // 如果未选择，则添加到选择列表
                     setSelectedDates([...selectedDates, date]);
                   }
                 }
               }}
               style={{ width: '100%', marginBottom: '8px' }}
             />
             <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
               {selectedDates.map((date, index) => (
                 <Tag
                   key={index}
                   closable
                   onClose={(e) => {
                     e.preventDefault();
                     setSelectedDates(selectedDates.filter((_, i) => i !== index));
                   }}
                   color="blue"
                 >
                   {date.format('MM-DD')}
                 </Tag>
               ))}
             </div>
           </div>
           <p style={{ marginTop: '8px', fontSize: '12px', color: '#888' }}>
             已选择 {selectedDates.length} 个日期
           </p>
         </div>
       </Modal>

       {/* 变更时间弹窗 - 按照ScheduCourse.js的逻辑 */}
       <Modal
         title="变更时间"
         open={changeTimeModalVisible}
         onCancel={() => {
           // 清理状态
           setChangeTimeEntryId(null);
           setSelectedNewDate(null);
           setSelectedNewTime(null);
           setChangeTimeModalVisible(false);
         }}
         footer={[
           <Button key="cancel" onClick={() => setChangeTimeModalVisible(false)}>
             取消
           </Button>,
           <Button key="confirm" type="primary" onClick={async () => {
             try {
               await handleChangeTimeConfirm();
               // 成功后清理状态
               setChangeTimeEntryId(null);
               setSelectedNewDate(null);
               setSelectedNewTime(null);
               setChangeTimeModalVisible(false);
             } catch (error) {
               // 错误情况下不清理状态，让用户重新选择
             }
           }}>
             确认变更
           </Button>,
         ]}
       >
         <Space direction="vertical" style={{ width: '100%' }}>
           <div>
             <Text>选择新日期：</Text>
             <DatePicker
               value={selectedNewDate}
               onChange={setSelectedNewDate}
               style={{ width: '100%' }}
               format="YYYY-MM-DD"
             />
           </div>
           <div>
             <Text>选择新时间：</Text>
             <Select
               placeholder="请选择时间"
               value={selectedNewTime ? selectedNewTime.format('HH:mm') : undefined}
               onChange={(value) => {
                 if (value) {
                   const [hour, minute] = value.split(':').map(Number);
                   const baseDate = selectedNewDate || currentDate;
                   const newTime = baseDate.hour(hour).minute(minute);
                   setSelectedNewTime(newTime);
                 }
               }}
               style={{ width: '100%' }}
             >
               {timeSlots.map((slot) => (
                 <Select.Option key={slot.slotId} value={slot.startTime.format('HH:mm')}>
                   {slot.label}
                 </Select.Option>
               ))}
             </Select>
           </div>
         </Space>
       </Modal>
     </div>
   );
 };

 export default SingleMemberScheduler;

